"use client";

import { useState, useCallback } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight } from "lucide-react";
const premiumValues = [{
  id: 1,
  title: "Partnership with Parents",
  description: "Clear communication, shared goals, and transparency at every stage."
}, {
  id: 2,
  title: "High Standards with Care",
  description: "Supportive and encouraging teaching combined with academic rigour."
}, {
  id: 3,
  title: "Confidence Before Performance",
  description: "Students perform best when they feel calm, capable, and understood."
}, {
  id: 4,
  title: "Personalised Support",
  description: "Every student is treated as an individual, never a one-size-fits-all approach."
}, {
  id: 5,
  title: "Long Term Development",
  description: "Building skills, habits, and independence that last beyond exams."
}];
export default function OurValues() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const handlePrev = useCallback(() => {
    setCurrentIndex(prev => prev === 0 ? premiumValues.length - 1 : prev - 1);
  }, []);
  const handleNext = useCallback(() => {
    setCurrentIndex(prev => prev === premiumValues.length - 1 ? 0 : prev + 1);
  }, []);
  return <>
      <style>
        {`
          @keyframes gentle-flash {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.85; }
          }
          .flash-card {
            animation: gentle-flash 3s ease-in-out infinite;
          }
        `}
      </style>
      <section ref={ref} className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="mx-auto max-w-4xl">
          <div className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="shadow-medium border border-border">
              <CardHeader>
                <CardTitle className="text-3xl sm:text-4xl text-primary text-center">
                  Our Values
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 text-foreground leading-relaxed">
                <p className="text-base sm:text-lg">
                  At Education Family Tutors, we believe that strong relationships are the foundation of effective learning. We take the time to understand each student's unique strengths, challenges, and aspirations, creating a supportive environment where they feel heard, valued, and motivated to succeed.
                </p>
                <p className="text-base sm:text-lg">
                  Trust and care are at the heart of everything we do. We work closely with parents to ensure open communication and regular progress updates, so families are always informed and involved in their child's educational journey. Our goal is to build confidence, resilience, and a genuine love for learning.
                </p>
                <p className="text-base sm:text-lg">
                  We are committed to being partners with parents in their child's development. By combining expert academic tutoring with personalised coaching, we help students not only achieve better grades but also develop the mindset, focus, and skills they need to thrive in school and beyond. Together, we are building a trusted education community where every student can reach their full potential.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12">
            <div className="relative">
              <div className="overflow-hidden flex justify-center">
                <div className="flex transition-transform duration-500 ease-out" style={{
                transform: `translateX(-${currentIndex * 100}%)`
              }}>
                  {premiumValues.map((value, i) => <div key={value.id} data-index={i} className="flex-shrink-0 px-2 flex justify-center" style={{
                  width: "100%"
                }}>
                      <Card className="flash-card shadow-medium border-2 border-secondary bg-secondary-light hover:shadow-strong transition-shadow duration-300 h-full max-w-md mx-auto">
                        <CardHeader>
                          <CardTitle className="text-xl text-primary text-center">
                            {value.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-foreground leading-relaxed text-center">
                            {value.description}
                          </p>
                        </CardContent>
                      </Card>
                    </div>)}
                </div>
              </div>

              <button onClick={handlePrev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 rounded-full bg-primary text-foreground-primary hover:bg-primary-hover shadow-medium hover:shadow-strong transition-all duration-300 flex items-center justify-center" aria-label="Previous value">
                <ChevronLeft className="w-5 h-5" />
              </button>

              <button onClick={handleNext} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 rounded-full bg-primary text-foreground-primary hover:bg-primary-hover shadow-medium hover:shadow-strong transition-all duration-300 flex items-center justify-center" aria-label="Next value">
                <ChevronRight className="w-5 h-5" />
              </button>

              <div className="flex justify-center gap-2 mt-6">
                {premiumValues.map((_, i) => <button key={i} onClick={() => setCurrentIndex(i)} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === currentIndex ? "bg-primary w-8" : "bg-muted hover:bg-primary/50"}`} aria-label={`Go to value ${i + 1}`} />)}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}